/**
 * Predictiveness analysis: do the composite macro scores foreshadow what the
 * S&P 500 does next?
 *
 * Pure, framework-free, no network — operates only on the two arrays the macro
 * dashboard already returns:
 *   - scoreHistory: [{date, economic_health, market_sentiment}]  (monthly, ~10yr)
 *   - spyHistory:   [{date, close}]                              (daily, ~25yr)
 *
 * Outcome metric (per product spec): a "pullback" / "shoot-up" is measured
 * relative to the S&P's TRAILING 6-MONTH high/low at the evaluation date, not as
 * a raw return. Predictiveness comes from the forward horizon: a score observed
 * at month D is matched to the pullback/run state at T = D + horizon.
 *
 *   pullbackPct(T) = (close_T - trailingHigh6mo(T)) / trailingHigh6mo(T) * 100   (<= 0)
 *   runPct(T)      = (close_T - trailingLow6mo(T))  / trailingLow6mo(T)  * 100   (>= 0)
 *
 * No look-ahead beyond the horizon: if T is past the last SPY bar we have, the
 * observation is dropped (never fabricated), mirroring macro_backtest.py.
 */

import { scoreColor } from './macroScore';

const TRAILING_MONTHS = 6;

// Bucket definitions — Low (<40) / Mid (40-60) / High (>60). `midColor` is the
// representative color used to tint the bucket row (via the shared scoreColor).
export const BUCKETS = [
  { key: 'low', label: 'Low (<40)', test: (v) => v < 40, midColor: scoreColor(30) },
  { key: 'mid', label: 'Mid (40–60)', test: (v) => v >= 40 && v <= 60, midColor: scoreColor(50) },
  { key: 'high', label: 'High (>60)', test: (v) => v > 60, midColor: scoreColor(75) },
];

export const HORIZONS = [
  { key: 1, label: '1M' },
  { key: 3, label: '3M' },
  { key: 6, label: '6M' },
  { key: 12, label: '12M' },
];

export const SCORE_KEYS = [
  { key: 'market_sentiment', label: 'Market Sentiment', color: '#60a5fa' },
  { key: 'economic_health', label: 'Economic Health', color: '#34d399' },
];

/** Shift an ISO date (YYYY-MM-DD) by n months, in UTC. */
export function addMonthsISO(dateISO, n) {
  const d = new Date(`${String(dateISO).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

/** Last close on/before dateISO, or null. History is ascending by date. */
export function priceAsOf(spyHistory, dateISO) {
  let latest = null;
  for (const h of spyHistory || []) {
    const dt = (h.date || '').slice(0, 10);
    if (dt <= dateISO) latest = h.close;
    else break;
  }
  return latest != null ? Number(latest) : null;
}

/** {high, low} of closes in (dateISO - months, dateISO]; nulls if window empty. */
export function trailingExtremes(spyHistory, dateISO, months = TRAILING_MONTHS) {
  const start = addMonthsISO(dateISO, -months);
  let high = null;
  let low = null;
  for (const h of spyHistory || []) {
    const dt = (h.date || '').slice(0, 10);
    if (dt <= start) continue;
    if (dt > dateISO) break;
    const c = Number(h.close);
    if (!Number.isFinite(c)) continue;
    if (high == null || c > high) high = c;
    if (low == null || c < low) low = c;
  }
  return { high, low };
}

/** Pearson correlation of two equal-length numeric arrays, or null. */
function pearson(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i += 1) { sx += xs[i]; sy += ys[i]; }
  const mx = sx / n;
  const my = sy / n;
  let num = 0;
  let dx2 = 0;
  let dy2 = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    num += dx * dy;
    dx2 += dx * dx;
    dy2 += dy * dy;
  }
  const den = Math.sqrt(dx2 * dy2);
  if (!den) return null;
  return num / den;
}

const round = (v, dp = 1) => {
  if (v == null || !Number.isFinite(v)) return null;
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};

const share = (arr, pred) => (arr.length ? arr.filter(pred).length / arr.length : null);

/**
 * Core analysis.
 *
 * @param {Array<{date,economic_health,market_sentiment}>} scoreHistory
 * @param {Array<{date,close}>} spyHistory
 * @param {{scoreKey:string, horizonMonths:number}} opts
 * @returns {{
 *   buckets: Array<object>, overallN: number, correlation: number|null,
 *   chartSeries: Array<{date,economic,sentiment,spy}>,
 *   scoreKey: string, horizonMonths: number, hasData: boolean
 * }}
 */
export function computePredictive(scoreHistory, spyHistory, { scoreKey, horizonMonths }) {
  const spy = spyHistory || [];
  const lastSpyDate = spy.length ? (spy[spy.length - 1].date || '').slice(0, 10) : null;

  // Per-bucket accumulators of observations.
  const obsByBucket = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
  const allScores = [];
  const allPullbacks = [];

  for (const point of scoreHistory || []) {
    const score = point?.[scoreKey];
    if (score == null) continue;
    const evalDate = addMonthsISO(point.date, horizonMonths);
    if (!evalDate || !lastSpyDate || evalDate > lastSpyDate) continue; // incomplete horizon

    const close = priceAsOf(spy, evalDate);
    const { high, low } = trailingExtremes(spy, evalDate);
    if (close == null || !high || !low) continue;

    const pullbackPct = ((close - high) / high) * 100; // <= 0
    const runPct = ((close - low) / low) * 100;        // >= 0

    const bucket = BUCKETS.find((b) => b.test(score));
    if (!bucket) continue;
    obsByBucket[bucket.key].push({ pullbackPct, runPct });
    allScores.push(score);
    allPullbacks.push(pullbackPct);
  }

  const buckets = BUCKETS.map((b) => {
    const obs = obsByBucket[b.key];
    const pulls = obs.map((o) => o.pullbackPct);
    const runs = obs.map((o) => o.runPct);
    const mean = (xs) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
    return {
      key: b.key,
      label: b.label,
      color: b.midColor,
      n: obs.length,
      avgPullback: round(mean(pulls)),
      avgRun: round(mean(runs)),
      pPullback05: round(share(pulls, (p) => p <= -5) * 100, 0),
      pPullback10: round(share(pulls, (p) => p <= -10) * 100, 0),
      pPullback20: round(share(pulls, (p) => p <= -20) * 100, 0),
      pRun10: round(share(runs, (r) => r >= 10) * 100, 0),
      pRun20: round(share(runs, (r) => r >= 20) * 100, 0),
    };
  });

  // Overlay series: each score month with its SPY close at that date.
  const chartSeries = (scoreHistory || []).map((p) => ({
    date: p.date,
    economic: p.economic_health,
    sentiment: p.market_sentiment,
    spy: priceAsOf(spy, (p.date || '').slice(0, 10)),
  }));

  return {
    buckets,
    overallN: allScores.length,
    correlation: round(pearson(allScores, allPullbacks), 2),
    chartSeries,
    scoreKey,
    horizonMonths,
    hasData: allScores.length > 0,
  };
}

/** Year-start ticks (first month of each distinct year) for the overlay XAxis. */
export function yearTicks(chartSeries) {
  const ticks = [];
  const seen = new Set();
  for (const d of chartSeries || []) {
    const yr = (d.date || '').slice(0, 4);
    if (yr && !seen.has(yr)) {
      seen.add(yr);
      ticks.push(d.date);
    }
  }
  return ticks;
}

// ===========================================================================
// PHASE 2 — Statistical proof: does a score predict the S&P's FORWARD RETURN?
//
// Outcome here is forward TOTAL RETURN (overall market price change) over a
// horizon, not the trailing-high/low framing above. The headline claim is the
// 1-month horizon: month-end sampling makes 1-month-forward returns
// non-overlapping, so ordinary significance tests are valid. Longer horizons
// overlap, so their regression p-values use Newey-West (HAC) standard errors.
// ===========================================================================

const mean = (a) => (a && a.length ? a.reduce((s, x) => s + x, 0) / a.length : null);

/**
 * Forward-return observations: [{date, evalDate, score, ret}] where ret is the
 * S&P % change from the score date to score date + horizon. Observations whose
 * horizon runs past the last SPY bar are dropped (no fabrication).
 */
export function forwardReturnSeries(scoreHistory, spyHistory, { scoreKey, horizonMonths }) {
  const spy = spyHistory || [];
  const lastSpyDate = spy.length ? (spy[spy.length - 1].date || '').slice(0, 10) : null;
  const points = [];
  for (const p of scoreHistory || []) {
    const score = p?.[scoreKey];
    if (score == null) continue;
    const d = (p.date || '').slice(0, 10);
    const evalDate = addMonthsISO(d, horizonMonths);
    if (!evalDate || !lastSpyDate || evalDate > lastSpyDate) continue;
    const p0 = priceAsOf(spy, d);
    const p1 = priceAsOf(spy, evalDate);
    if (p0 == null || p1 == null || p0 === 0) continue;
    points.push({ date: d, evalDate, score, ret: ((p1 - p0) / p0) * 100 });
  }
  return points;
}

// --- Rank correlation (Information Coefficient) ----------------------------

/** Fractional ranks with average ties (1-based). */
function rankArray(xs) {
  const idx = xs.map((v, i) => [v, i]).sort((a, b) => a[0] - b[0]);
  const ranks = new Array(xs.length);
  let i = 0;
  while (i < idx.length) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1][0] === idx[i][0]) j += 1;
    const avg = (i + j) / 2 + 1;
    for (let k = i; k <= j; k += 1) ranks[idx[k][1]] = avg;
    i = j + 1;
  }
  return ranks;
}

export function spearman(xs, ys) {
  if (!xs || !ys || xs.length !== ys.length || xs.length < 3) return null;
  return pearson(rankArray(xs), rankArray(ys));
}

// --- Significance helpers (Student-t, normal) ------------------------------

function gammaln(x) {
  const cof = [76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
  let y = x;
  let tmp = x + 5.5;
  tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j += 1) { y += 1; ser += cof[j] / y; }
  return -tmp + Math.log((2.5066282746310005 * ser) / x);
}

function betacf(a, b, x) {
  const FPMIN = 1e-300;
  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;
  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < FPMIN) d = FPMIN;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= 200; m += 1) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d; if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c; if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d; h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d; if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c; if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d;
    const del = d * c; h *= del;
    if (Math.abs(del - 1) < 1e-12) break;
  }
  return h;
}

/** Regularized incomplete beta I_x(a,b). */
export function incompleteBeta(x, a, b) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bt = Math.exp(
    gammaln(a + b) - gammaln(a) - gammaln(b) + a * Math.log(x) + b * Math.log(1 - x),
  );
  if (x < (a + 1) / (a + b + 2)) return (bt * betacf(a, b, x)) / a;
  return 1 - (bt * betacf(b, a, 1 - x)) / b;
}

/** Two-sided p-value for a t-statistic with df degrees of freedom. */
export function studentTwoSidedP(t, df) {
  if (df <= 0 || !Number.isFinite(t)) return null;
  const x = df / (df + t * t);
  return incompleteBeta(x, df / 2, 0.5);
}

/** Standard normal CDF (Abramowitz & Stegun 7.1.26). */
export function normalCdf(z) {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  let p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937
    + t * (-1.821255978 + t * 1.330274429))));
  if (z > 0) p = 1 - p;
  return p;
}

// --- OLS with Newey-West (HAC) standard errors -----------------------------

/**
 * Simple OLS y = a + b·x with HAC SE on the slope (lag = max(0, horizon-1)).
 * lag 0 reduces to White heteroskedasticity-robust SE. Returns slope, R²,
 * HAC t-stat and two-sided p-value.
 */
export function olsHAC(xs, ys, lag = 0) {
  const n = xs.length;
  if (n < 3 || ys.length !== n) return null;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i += 1) { sx += xs[i]; sy += ys[i]; }
  const mx = sx / n;
  const my = sy / n;
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    sxx += dx * dx; sxy += dx * dy; syy += dy * dy;
  }
  if (sxx === 0) return null;
  const b = sxy / sxx;
  const a = my - b * mx;
  const r2 = syy === 0 ? null : (sxy * sxy) / (sxx * syy);

  // HAC "meat": u_t = (x_t - mx) * residual_t
  const u = new Array(n);
  for (let i = 0; i < n; i += 1) u[i] = (xs[i] - mx) * (ys[i] - (a + b * xs[i]));
  let S = 0;
  for (let i = 0; i < n; i += 1) S += u[i] * u[i];
  const L = Math.max(0, lag);
  for (let l = 1; l <= L; l += 1) {
    const w = 1 - l / (L + 1); // Bartlett kernel
    let g = 0;
    for (let i = l; i < n; i += 1) g += u[i] * u[i - l];
    S += 2 * w * g;
  }
  const seB = Math.sqrt(Math.max(S / (sxx * sxx), 0));
  const tB = seB > 0 ? b / seB : null;
  const df = n - 2;
  return { a, b, r2, seB, tB, pB: tB == null ? null : studentTwoSidedP(tB, df), n };
}

// --- Tercile monotonicity + bootstrap CI -----------------------------------

/** Deterministic RNG so the bootstrap CI is reproducible/testable. */
function mulberry32(seed) {
  let s = seed >>> 0;
  return function next() {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function quantileSorted(sorted, q) {
  if (!sorted.length) return null;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/**
 * Mean forward return by score tercile (balanced n via sample-quantile cuts),
 * the top-minus-bottom spread, whether means are monotone in score, and a
 * bootstrap 90% CI on the spread.
 */
export function terciles(points, { bootstrap = 1000, seed = 1234567 } = {}) {
  const n = points.length;
  if (n < 6) return null;
  const sortedScores = points.map((p) => p.score).slice().sort((a, b) => a - b);
  const c1 = quantileSorted(sortedScores, 1 / 3);
  const c2 = quantileSorted(sortedScores, 2 / 3);

  const lowR = [];
  const midR = [];
  const highR = [];
  for (const p of points) {
    if (p.score <= c1) lowR.push(p.ret);
    else if (p.score > c2) highR.push(p.ret);
    else midR.push(p.ret);
  }
  const mLow = mean(lowR);
  const mMid = mean(midR);
  const mHigh = mean(highR);
  const spread = mHigh != null && mLow != null ? mHigh - mLow : null;
  const monotonic = mLow != null && mMid != null && mHigh != null
    && ((mLow <= mMid && mMid <= mHigh) || (mLow >= mMid && mMid >= mHigh));

  // Bootstrap the spread (fixed cuts; resample with replacement).
  const rng = mulberry32(seed);
  const spreads = [];
  for (let bI = 0; bI < bootstrap; bI += 1) {
    let sLow = 0;
    let nLow = 0;
    let sHigh = 0;
    let nHigh = 0;
    for (let i = 0; i < n; i += 1) {
      const p = points[(rng() * n) | 0];
      if (p.score <= c1) { sLow += p.ret; nLow += 1; }
      else if (p.score > c2) { sHigh += p.ret; nHigh += 1; }
    }
    if (nLow && nHigh) spreads.push(sHigh / nHigh - sLow / nLow);
  }
  spreads.sort((a, b) => a - b);

  return {
    cuts: [round(c1, 1), round(c2, 1)],
    buckets: [
      { label: 'Bottom third', n: lowR.length, meanRet: round(mLow, 2) },
      { label: 'Middle third', n: midR.length, meanRet: round(mMid, 2) },
      { label: 'Top third', n: highR.length, meanRet: round(mHigh, 2) },
    ],
    spread: round(spread, 2),
    spreadCI: spreads.length
      ? [round(quantileSorted(spreads, 0.05), 2), round(quantileSorted(spreads, 0.95), 2)]
      : null,
    monotonic,
  };
}

// --- Out-of-sample walk-forward (the crux of "proof") ----------------------

/**
 * Expanding-window walk-forward with strict no-lookahead: to predict the obs at
 * score date D, train OLS only on observations whose outcome was already
 * realized by D (evalDate <= D). Returns OOS R² (vs the expanding training
 * mean), directional hit-rate vs the best constant-guess baseline, and the
 * mean realized return when the model predicts up vs down.
 */
export function walkForward(points, { minTrain = 36 } = {}) {
  const pts = points.slice().sort((a, b) => (a.date < b.date ? -1 : 1));
  const preds = [];
  for (let i = 0; i < pts.length; i += 1) {
    const D = pts[i].date;
    const train = pts.filter((p) => p.evalDate <= D);
    if (train.length < minTrain) continue;
    const m = train.length;
    let sx = 0;
    let sy = 0;
    for (let k = 0; k < m; k += 1) { sx += train[k].score; sy += train[k].ret; }
    const mx = sx / m;
    const trainMean = sy / m;
    let sxx = 0;
    let sxy = 0;
    for (let k = 0; k < m; k += 1) {
      const dx = train[k].score - mx;
      sxx += dx * dx; sxy += dx * (train[k].ret - trainMean);
    }
    const b = sxx > 0 ? sxy / sxx : 0;
    const a = trainMean - b * mx;
    preds.push({ date: D, yhat: a + b * pts[i].score, ret: pts[i].ret, trainMean });
  }

  const nOOS = preds.length;
  const empty = {
    nOOS, oosR2: null, hitRate: null, baselineHitRate: null,
    binomP: null, meanWhenUp: null, meanWhenDown: null,
  };
  if (nOOS < 8) return empty;

  let ssRes = 0;
  let ssTot = 0;
  let hits = 0;
  let ups = 0;
  const upRets = [];
  const downRets = [];
  for (const p of preds) {
    ssRes += (p.ret - p.yhat) ** 2;
    ssTot += (p.ret - p.trainMean) ** 2;
    const predUp = p.yhat > 0;
    const actualUp = p.ret > 0;
    if (predUp === actualUp) hits += 1;
    if (actualUp) ups += 1;
    if (predUp) upRets.push(p.ret); else downRets.push(p.ret);
  }
  // Binomial test of hit-rate vs 0.5 (normal approx, continuity corrected).
  const z = (Math.abs(hits - nOOS * 0.5) - 0.5) / Math.sqrt(nOOS * 0.25);
  return {
    nOOS,
    oosR2: round(ssTot > 0 ? 1 - ssRes / ssTot : null, 3),
    hitRate: round((hits / nOOS) * 100, 1),
    baselineHitRate: round((Math.max(ups, nOOS - ups) / nOOS) * 100, 1),
    binomP: round(2 * (1 - normalCdf(z)), 3),
    meanWhenUp: round(mean(upRets), 2),
    meanWhenDown: round(mean(downRets), 2),
  };
}

// --- Verdict ----------------------------------------------------------------

/**
 * Combine the evidence into a weak/moderate/strong/none verdict with a
 * plain-English rationale. OOS R²>0 is required for "strong".
 */
export function assessEvidence({ ic, icP, oos, terc }) {
  const passInSample = icP != null && icP < 0.05 && ic != null && Math.abs(ic) > 0;
  const passOosR2 = oos?.oosR2 != null && oos.oosR2 > 0;
  const passHit = oos?.hitRate != null && oos?.baselineHitRate != null
    && oos.hitRate > oos.baselineHitRate && oos?.binomP != null && oos.binomP < 0.05;
  const passMono = terc?.monotonic === true && Array.isArray(terc?.spreadCI)
    && (terc.spreadCI[0] > 0 || terc.spreadCI[1] < 0); // CI excludes 0

  const reasons = [];
  if (passInSample) reasons.push('in-sample rank correlation is significant (p<0.05)');
  if (passOosR2) reasons.push('beats a naive average out-of-sample (OOS R²>0)');
  if (passHit) reasons.push('out-of-sample direction beats the best constant guess');
  if (passMono) reasons.push('returns rise monotonically across score terciles (CI excludes 0)');

  const score = (passInSample ? 1 : 0) + (passOosR2 ? 1 : 0) + (passHit ? 1 : 0) + (passMono ? 1 : 0);
  let level;
  if (passOosR2 && score >= 3) level = 'strong';
  else if (score >= 2) level = 'moderate';
  else if (score >= 1) level = 'weak';
  else level = 'none';

  return {
    level,
    score,
    reasons: reasons.length ? reasons : ['no statistically reliable forward-return signal detected'],
    passInSample,
    passOosR2,
    passHit,
    passMono,
  };
}

/**
 * One-call bundle for the "Predictive proof" view. Returns hasData=false when
 * there aren't enough forward-return observations to test.
 */
export function computeProof(scoreHistory, spyHistory, { scoreKey, horizonMonths }) {
  const points = forwardReturnSeries(scoreHistory, spyHistory, { scoreKey, horizonMonths });
  const n = points.length;
  if (n < 12) return { hasData: false, n, horizonMonths, scoreKey };

  const scores = points.map((p) => p.score);
  const rets = points.map((p) => p.ret);
  const ic = spearman(scores, rets);
  let icT = null;
  if (ic != null && Math.abs(ic) < 1) icT = ic * Math.sqrt((n - 2) / (1 - ic * ic));
  const icP = icT != null ? studentTwoSidedP(icT, n - 2) : null;

  const ols = olsHAC(scores, rets, Math.max(0, horizonMonths - 1));
  const terc = terciles(points);
  const oos = walkForward(points, { minTrain: 36 });
  const verdict = assessEvidence({ ic, icP, oos, terc });

  return {
    hasData: true,
    n,
    points,
    ic: round(ic, 2),
    icP: round(icP, 3),
    pearson: round(pearson(scores, rets), 2),
    ols,
    terc,
    oos,
    verdict,
    overlapping: horizonMonths > 1,
    horizonMonths,
    scoreKey,
  };
}
